import React, { useEffect, useMemo, useState } from "react";
import { api, clearToken, getApiBase, getToken, setToken } from "../api";

type Tab = "services" | "transactions" | "events" | "metrics";

export function App(){
  const [token, setTok] = useState(getToken());
  const [role, setRole] = useState<string>("");
  const [tab, setTab] = useState<Tab>("services");
  const apiBase = useMemo(() => getApiBase(), []);

  useEffect(() => { setTok(getToken()); }, []);

  const onLogout = () => { clearToken(); setTok(""); setRole(""); };

  if (!apiBase) {
    return (
      <div className="container">
        <div className="card">
          <h2>Config missing</h2>
          <p className="muted">Set <span className="mono">VITE_API_BASE</span> in Cloudflare Pages (or .env) to your Worker URL.</p>
        </div>
      </div>
    );
  }

  if (!token) return <Login onAuthed={(t, r) => { setToken(t); setTok(t); setRole(r); }} />;

  return (
    <div className="container">
      <div className="card">
        <div className="row">
          <h2 style={{margin:0}}>Central Payment Admin</h2>
          <span className="badge right">API: <span className="mono">{apiBase}</span></span>
          <span className="badge">Role: <span className="mono">{role || "?"}</span></span>
          <button onClick={onLogout}>Logout</button>
        </div>
        <div className="row" style={{marginTop:10}}>
          <button className={tab==="services" ? "primary" : ""} onClick={() => setTab("services")}>Services</button>
          <button className={tab==="transactions" ? "primary" : ""} onClick={() => setTab("transactions")}>Transactions</button>
          <button className={tab==="events" ? "primary" : ""} onClick={() => setTab("events")}>Events</button>
          <button className={tab==="metrics" ? "primary" : ""} onClick={() => setTab("metrics")}>Metrics</button>
        </div>
      </div>

      {tab==="services" && <Services />}
      {tab==="transactions" && <Transactions role={role} />}
      {tab==="events" && <Events />}
      {tab==="metrics" && <Metrics />}
    </div>
  );
}

function Login({ onAuthed }:{ onAuthed:(token:string, role:string)=>void }){
  const [username, setUsername] = useState("admin");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");

  const submit = async () => {
    setErr("");
    const { res, json } = await api("/admin/login", { method:"POST", body: JSON.stringify({ username, password }) });
    if (!res.ok) { setErr(json?.error || "login_failed"); return; }
    onAuthed(json.token, json.role);
  };

  return (
    <div className="container">
      <div className="card">
        <h2>Login</h2>
        <div className="row">
          <input value={username} onChange={e=>setUsername(e.target.value)} placeholder="username" />
          <input value={password} onChange={e=>setPassword(e.target.value)} placeholder="password" type="password" />
          <button className="primary" onClick={submit}>Login</button>
        </div>
        {err && <p className="muted">Error: <span className="mono">{err}</span></p>}
      </div>
    </div>
  );
}

function Services(){
  const [rows, setRows] = useState<any[]>([]);
  const [msg, setMsg] = useState("");
  const [form, setForm] = useState({ service_id:"", service_name:"", callback_url:"", return_url:"" });

  const load = async () => {
    const { res, json } = await api("/admin/services");
    if (!res.ok) { setMsg(json?.error || "load_failed"); return; }
    setRows(json.services || []);
  };

  useEffect(() => { load(); }, []);

  const create = async () => {
    setMsg("");
    const payload:any = { ...form };
    if (!payload.service_id) delete payload.service_id;
    if (!payload.return_url) delete payload.return_url;
    const { res, json } = await api("/admin/services", { method:"POST", body: JSON.stringify(payload) });
    if (!res.ok) { setMsg(json?.error || "create_failed"); return; }
    setMsg(`Created ${json.service_id}. SAVE THIS ONCE: api_key=${json.api_key} secret=${json.service_secret}`);
    await load();
  };

  const rotate = async (id:string) => {
    setMsg("");
    const { res, json } = await api(`/admin/services/${id}/rotate-key`, { method:"POST", body:"{}" });
    if (!res.ok) { setMsg(json?.error || "rotate_failed"); return; }
    setMsg(`Rotated ${id}. SAVE THIS ONCE: api_key=${json.api_key} secret=${json.service_secret}`);
    await load();
  };

  const toggle = async (id:string, active:boolean) => {
    await api(`/admin/services/${id}`, { method:"PATCH", body: JSON.stringify({ is_active: active }) });
    await load();
  };

  return (
    <div className="card">
      <h3>Services</h3>
      <div className="row">
        <input value={form.service_id} onChange={e=>setForm({...form, service_id:e.target.value})} placeholder="service_id (optional)" />
        <input value={form.service_name} onChange={e=>setForm({...form, service_name:e.target.value})} placeholder="service_name" />
        <input value={form.callback_url} onChange={e=>setForm({...form, callback_url:e.target.value})} placeholder="callback_url" style={{minWidth:320}} />
        <input value={form.return_url} onChange={e=>setForm({...form, return_url:e.target.value})} placeholder="return_url (optional)" style={{minWidth:320}} />
        <button className="primary" onClick={create}>Create</button>
        <button onClick={load}>Refresh</button>
      </div>
      {msg && <p className="muted">{msg}</p>}
      <table>
        <thead><tr><th>ID</th><th>Name</th><th>Callback</th><th>Return</th><th>Active</th><th>Actions</th></tr></thead>
        <tbody>
          {rows.map(r => (
            <tr key={r.service_id}>
              <td className="mono">{r.service_id}</td>
              <td>{r.service_name}</td>
              <td className="mono">{r.callback_url}</td>
              <td className="mono">{r.return_url || ""}</td>
              <td>{r.is_active===1 ? "YES" : "NO"}</td>
              <td>
                <button onClick={() => rotate(r.service_id)}>Rotate</button>{" "}
                <button onClick={() => toggle(r.service_id, r.is_active!==1)}>{r.is_active===1 ? "Disable" : "Enable"}</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Transactions({ role }:{ role:string }){
  const [rows, setRows] = useState<any[]>([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [fst, setFst] = useState("");
  const [sid, setSid] = useState("");
  const [offset, setOffset] = useState("0");
  const [detail, setDetail] = useState<any>(null);
  const [events, setEvents] = useState<any[]>([]);
  const [modal, setModal] = useState<{title:string; sub:string; body:string} | null>(null);

  const load = async () => {
    const qs = new URLSearchParams();
    if (q) qs.set("q", q);
    if (status) qs.set("status", status);
    if (fst) qs.set("forward_status", fst);
    if (sid) qs.set("service_id", sid);
    qs.set("limit", "50");
    if (offset) qs.set("offset", offset);
    const { res, json } = await api("/admin/transactions?" + qs.toString());
    if (!res.ok) return;
    setRows(json.rows || []);
  };

  useEffect(() => { load(); }, []);

  const view = async (id:string) => {
    const { res, json } = await api(`/admin/transactions/${id}`);
    if (!res.ok) return;
    setDetail(json.payment);
    setEvents(json.events || []);
  };

  const retry = async (id:string) => {
    if (!confirm("Manual retry forward?")) return;
    const { res, json } = await api(`/admin/transactions/${id}/retry`, { method:"POST", body:"{}" });
    alert(res.ok ? "OK" : (json?.error || "failed"));
    await load();
    await view(id);
  };

  const openJson = (title:string, sub:string, payload:string) => {
    let pretty = payload;
    try { pretty = JSON.stringify(JSON.parse(payload), null, 2); } catch {}
    setModal({ title, sub, body: pretty });
  };

  return (
    <div className="card">
      <h3>Transactions</h3>
      <div className="row">
        <input value={q} onChange={e=>setQ(e.target.value)} placeholder="search (order/ref)" />
        <select value={status} onChange={e=>setStatus(e.target.value)}>
          <option value="">status (all)</option>
          <option value="PENDING">PENDING</option>
          <option value="PAID">PAID</option>
          <option value="FAILED">FAILED</option>
        </select>
        <select value={fst} onChange={e=>setFst(e.target.value)}>
          <option value="">forward (all)</option>
          <option value="FORWARDED">FORWARDED</option>
          <option value="FAILED">FAILED</option>
          <option value="NONE">(none)</option>
        </select>
        <input value={sid} onChange={e=>setSid(e.target.value)} placeholder="service_id" />
        <input value={offset} onChange={e=>setOffset(e.target.value)} placeholder="offset" style={{width:90}} />
        <button className="primary" onClick={load}>Load</button>
      </div>

      <table>
        <thead><tr><th>Order</th><th>Service</th><th>Amount</th><th>Status</th><th>Forward</th><th>Actions</th></tr></thead>
        <tbody>
          {rows.map(r => (
            <tr key={r.central_order_id}>
              <td className="mono">{r.central_order_id}</td>
              <td className="mono">{r.service_id}</td>
              <td>{r.payment_amount}</td>
              <td className="mono">{r.status}</td>
              <td className="mono">{r.forward_status || ""}</td>
              <td>
                <button onClick={() => view(r.central_order_id)}>View</button>{" "}
                {role!=="VIEWER" && <button onClick={() => retry(r.central_order_id)}>Retry</button>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {detail && (
        <div className="card" style={{marginTop:12}}>
          <div className="row">
            <h4 style={{margin:0}}>Detail</h4>
            <span className="badge">Order: <span className="mono">{detail.central_order_id}</span></span>
            <span className="badge">Status: <span className="mono">{detail.status}</span></span>
            <span className="badge">Forward: <span className="mono">{detail.forward_status||""}</span></span>
            <button className="right" onClick={() => setDetail(null)}>Close</button>
          </div>
          <p className="muted"><b>Payment URL</b>: {detail.duitku_payment_url ? <a href={detail.duitku_payment_url} target="_blank">open</a> : "(n/a)"}</p>

          <h4>Event Timeline</h4>
          <table>
            <thead><tr><th>Time</th><th>Type</th><th>Payload</th><th>Action</th></tr></thead>
            <tbody>
              {events.map((e, idx) => {
                const raw = String(e.payload_json||"");
                let short = raw;
                try { short = JSON.stringify(JSON.parse(raw)); } catch {}
                if (short.length > 240) short = short.slice(0,240) + "…";
                return (
                  <tr key={idx}>
                    <td>{e.created_at}</td>
                    <td className="mono">{e.event_type}</td>
                    <td className="mono">{short}</td>
                    <td><button onClick={() => openJson("Event "+e.event_type, "order: "+detail.central_order_id, raw)}>Full</button></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {modal && (
        <dialog open>
          <div style={{padding:14,borderBottom:"1px solid #eee",display:"flex",gap:10,alignItems:"center",justifyContent:"space-between"}}>
            <div>
              <div style={{fontWeight:700}}>{modal.title}</div>
              <div className="muted">{modal.sub}</div>
            </div>
            <div className="row">
              <button onClick={async()=>{ await navigator.clipboard.writeText(modal.body); alert("Copied"); }}>Copy</button>
              <button onClick={()=>setModal(null)}>Close</button>
            </div>
          </div>
          <pre className="mono" style={{padding:14,maxHeight:"70vh",overflow:"auto"}}>{modal.body}</pre>
        </dialog>
      )}
    </div>
  );
}

function Events(){
  const [rows, setRows] = useState<any[]>([]);
  const [q, setQ] = useState("");
  const [type, setType] = useState("");

  const load = async () => {
    const qs = new URLSearchParams();
    if (q) qs.set("q", q);
    if (type) qs.set("type", type);
    qs.set("limit","100");
    const { res, json } = await api("/admin/events?" + qs.toString());
    if (!res.ok) return;
    setRows(json.events || []);
  };

  useEffect(()=>{ load(); }, []);

  return (
    <div className="card">
      <h3>Events Search</h3>
      <div className="row">
        <input value={q} onChange={e=>setQ(e.target.value)} placeholder="search (order/payload)" />
        <select value={type} onChange={e=>setType(e.target.value)}>
          <option value="">(all)</option>
          <option value="CREATE">CREATE</option>
          <option value="DUITKU_CALLBACK">DUITKU_CALLBACK</option>
          <option value="DUITKU_STATUS_VERIFY">DUITKU_STATUS_VERIFY</option>
          <option value="FORWARD_ACK">FORWARD_ACK</option>
          <option value="FORWARD_FAIL">FORWARD_FAIL</option>
          <option value="RETRY_SCHEDULED">RETRY_SCHEDULED</option>
        </select>
        <button className="primary" onClick={load}>Search</button>
      </div>
      <table>
        <thead><tr><th>Time</th><th>Order</th><th>Type</th><th>Payload</th></tr></thead>
        <tbody>
          {rows.map((e, idx) => (
            <tr key={idx}>
              <td>{e.created_at}</td>
              <td className="mono">{e.central_order_id}</td>
              <td className="mono">{e.event_type}</td>
              <td className="mono">{String(e.payload_json||"").slice(0,240)}{String(e.payload_json||"").length>240?"…":""}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Metrics(){
  const [hours, setHours] = useState("24");
  const [rows, setRows] = useState<any[]>([]);

  const load = async () => {
    const { res, json } = await api(`/admin/metrics/forward-failures?window_hours=${encodeURIComponent(hours)}`);
    if (!res.ok) return;
    setRows(json.rows || []);
  };

  useEffect(()=>{ load(); }, []);

  return (
    <div className="card">
      <h3>Incident Metrics</h3>
      <div className="row">
        <select value={hours} onChange={e=>setHours(e.target.value)}>
          <option value="1">last 1h</option>
          <option value="6">last 6h</option>
          <option value="24">last 24h</option>
          <option value="72">last 72h</option>
          <option value="168">last 7d</option>
        </select>
        <button className="primary" onClick={load}>Refresh</button>
      </div>
      <table>
        <thead><tr><th>Service</th><th>Forward Failed</th></tr></thead>
        <tbody>
          {rows.length===0 && <tr><td colSpan={2} className="muted">No failures 🎉</td></tr>}
          {rows.map((r, idx) => (
            <tr key={idx}>
              <td className="mono">{r.service_id}</td>
              <td>{r.failed_count}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
